import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from "@learn-me/ui";
import { bundlesApi } from "@/features/bundles/api/bundlesApi";
import { adminApi } from "@/features/admin/api/adminApi";
import { ArrowLeft, Save, Package, X, GripVertical } from "lucide-react";
import toast from "react-hot-toast";
import { handleApiError } from "@/lib/errorHandler";

interface BundleFormData {
  name: string;
  description: string;
  shortDescription: string;
  thumbnailUrl: string;
  hasDiscount: boolean;
  price: number | null;
  originalPrice: number | null;
  currency: string;
  isPublished: boolean;
  position: number;
}

export default function BundleEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<BundleFormData>({
    defaultValues: {
      name: "",
      description: "",
      shortDescription: "",
      thumbnailUrl: "",
      hasDiscount: false,
      price: null,
      originalPrice: null,
      currency: "USD",
      isPublished: false,
      position: 0,
    },
  });

  const hasDiscount = watch("hasDiscount");
  const isPublished = watch("isPublished");

  // Cargar paquete existente
  const { data: bundle, isLoading: bundleLoading } = useQuery({
    queryKey: ["bundle", id],
    queryFn: () => bundlesApi.getById(id!),
    enabled: isEditing,
  });

  // Cargar lista de cursos disponibles
  const { data: coursesData } = useQuery({
    queryKey: ["admin-courses-all"],
    queryFn: () => adminApi.getCourses(1, 100),
  });

  useEffect(() => {
    if (bundle) {
      reset({
        name: bundle.name,
        description: bundle.description || "",
        shortDescription: bundle.shortDescription || "",
        thumbnailUrl: bundle.thumbnailUrl || "",
        hasDiscount: bundle.hasDiscount,
        price: bundle.price,
        originalPrice: bundle.originalPrice,
        currency: bundle.currency,
        isPublished: bundle.isPublished,
        position: bundle.position || 0,
      });
      setSelectedCourseIds(bundle.courses?.map((c) => c.id) || []);
    }
  }, [bundle, reset]);

  const createMutation = useMutation({
    mutationFn: bundlesApi.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin-bundles"] });
      toast.success("Paquete creado exitosamente");
      navigate(`/admin/bundles/${data.id}/edit`);
    },
    onError: (error) => {
      handleApiError(error, "Error al crear el paquete");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      bundlesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-bundles"] });
      queryClient.invalidateQueries({ queryKey: ["bundle", id] });
      toast.success("Paquete actualizado exitosamente");
    },
    onError: (error) => {
      handleApiError(error, "Error al actualizar el paquete");
    },
  });

  const updateCoursesMutation = useMutation({
    mutationFn: ({
      bundleId,
      courseIds,
    }: {
      bundleId: string;
      courseIds: string[];
    }) => bundlesApi.updateCourses(bundleId, courseIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bundle", id] });
      toast.success("Cursos actualizados");
    },
    onError: (error) => {
      handleApiError(error, "Error al actualizar los cursos");
    },
  });

  const onSubmit = (data: BundleFormData) => {
    const payload = {
      name: data.name,
      description: data.description || undefined,
      shortDescription: data.shortDescription || undefined,
      thumbnailUrl: data.thumbnailUrl || undefined,
      hasDiscount: data.hasDiscount,
      price: data.price || undefined,
      originalPrice: data.hasDiscount
        ? data.originalPrice || undefined
        : undefined,
      currency: data.currency,
      isPublished: data.isPublished,
      position: data.position,
      courseIds: isEditing ? undefined : selectedCourseIds,
    };

    if (isEditing && id) {
      updateMutation.mutate({ id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleSaveCourses = () => {
    if (id) {
      updateCoursesMutation.mutate({
        bundleId: id,
        courseIds: selectedCourseIds,
      });
    }
  };

  const toggleCourse = (courseId: string) => {
    setSelectedCourseIds((prev) =>
      prev.includes(courseId)
        ? prev.filter((id) => id !== courseId)
        : [...prev, courseId],
    );
  };

  const removeCourse = (courseId: string) => {
    setSelectedCourseIds((prev) => prev.filter((id) => id !== courseId));
  };

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    updateCoursesMutation.isPending;

  if (isEditing && bundleLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const availableCourses = coursesData?.items || [];
  const selectedCourses = availableCourses.filter((c) =>
    selectedCourseIds.includes(c.id),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/bundles")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
        <div>
          <h1 className="text-3xl font-bold">
            {isEditing ? "Editar Paquete" : "Nuevo Paquete"}
          </h1>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Formulario principal */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Información del Paquete</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="name" className="block text-sm font-medium">
                    Nombre *
                  </label>
                  <Input
                    id="name"
                    placeholder="Ej: Pack Desarrollador Full Stack"
                    {...register("name", {
                      required: "El nombre es requerido",
                      minLength: {
                        value: 2,
                        message: "El nombre debe tener al menos 2 caracteres",
                      },
                    })}
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="shortDescription"
                    className="block text-sm font-medium"
                  >
                    Descripción Corta
                  </label>
                  <Input
                    id="shortDescription"
                    placeholder="Breve descripción del paquete..."
                    maxLength={300}
                    {...register("shortDescription")}
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium"
                  >
                    Descripción Completa
                  </label>
                  <textarea
                    id="description"
                    className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="Descripción detallada del paquete..."
                    {...register("description")}
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="thumbnailUrl"
                    className="block text-sm font-medium"
                  >
                    URL de Imagen
                  </label>
                  <Input
                    id="thumbnailUrl"
                    type="url"
                    placeholder="https://..."
                    {...register("thumbnailUrl")}
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label
                      htmlFor="price"
                      className="block text-sm font-medium"
                    >
                      Precio
                    </label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="99.99"
                      {...register("price", { valueAsNumber: true })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label
                      htmlFor="currency"
                      className="block text-sm font-medium"
                    >
                      Moneda
                    </label>
                    <Input
                      id="currency"
                      placeholder="USD"
                      maxLength={3}
                      {...register("currency")}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <span className="text-sm font-medium">
                      ¿Tiene Descuento?
                    </span>
                    <p className="text-sm text-muted-foreground">
                      Mostrar precio original tachado
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={hasDiscount}
                    onClick={() => setValue("hasDiscount", !hasDiscount)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      hasDiscount ? "bg-primary" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        hasDiscount ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                {hasDiscount && (
                  <div className="space-y-2">
                    <label
                      htmlFor="originalPrice"
                      className="block text-sm font-medium"
                    >
                      Precio Original
                    </label>
                    <Input
                      id="originalPrice"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="149.99"
                      {...register("originalPrice", { valueAsNumber: true })}
                    />
                  </div>
                )}

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <span className="text-sm font-medium">Publicado</span>
                    <p className="text-sm text-muted-foreground">
                      Visible para los usuarios
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isPublished}
                    onClick={() => setValue("isPublished", !isPublished)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      isPublished ? "bg-primary" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        isPublished ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/admin/bundles")}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={isPending || (!isDirty && isEditing)}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isPending ? "Guardando..." : "Guardar"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Panel lateral: Cursos */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Cursos del Paquete
              </CardTitle>
              {isEditing && selectedCourseIds.length > 0 && (
                <Button
                  size="sm"
                  onClick={handleSaveCourses}
                  disabled={updateCoursesMutation.isPending}
                >
                  Guardar
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {/* Cursos seleccionados */}
              {selectedCourses.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {selectedCourses.map((course, index) => (
                    <div
                      key={course.id}
                      className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg"
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground w-6">
                        {index + 1}.
                      </span>
                      <span className="flex-1 text-sm truncate">
                        {course.title}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCourse(course.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground mb-4">
                  No hay cursos seleccionados
                </p>
              )}

              {/* Lista de cursos disponibles */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-2">Agregar cursos:</p>
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {availableCourses
                    .filter((c) => !selectedCourseIds.includes(c.id))
                    .map((course) => (
                      <button
                        key={course.id}
                        type="button"
                        onClick={() => toggleCourse(course.id)}
                        className="w-full text-left p-2 rounded hover:bg-muted/50 text-sm"
                      >
                        {course.title}
                      </button>
                    ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
